import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Typography from '../../components/Typography/Typography';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import { authAPI } from '../../services/api';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const validateEmail = () => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (validateEmail()) {
      setIsLoading(true);
      
      try {
        const response = await authAPI.forgotPassword({ email });
        toast.success(response.message || 'Verification code sent to your email');
        navigate('/verify-otp', { state: { email } });
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to send verification code. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  return (
    <div className='flex-1 flex items-center justify-center px-4 py-8'>
      <div className='w-full max-w-md mx-auto'>
        {/* Reset Password Modal */}
        <div className='!bg-[#FFFFFF] rounded-3xl border-2 border-[#E2E8F0] shadow-auth_box p-6 sm:p-8 w-full relative overflow-hidden'>
          {/* Decorative border gradient */}
          <div className='absolute inset-0 rounded-3xl pointer-events-none'></div>
          
          {/* Header */}
          <div className='text-center mb-6 sm:mb-8 relative z-10'>
            <Typography
              variant='h1'
              className='mb-2 text-blue-900 font-bold title text-2xl sm:text-2xl md:text-2xl lg:text-3xl'
            >
              Reset Password
            </Typography>
          
            <Typography variant='body1' color='grey' className='text-k-grey-text text-[16px]'>
              Enter your email address and we'll send you a verification code
            </Typography>
          </div>

          {/* Form */}
          <div className='space-y-4 sm:space-y-4 relative z-10'>
            {/* Email Field */}
            <TextField
              label='Email Address'
              type='email'
              value={email}
              onChange={handleEmailChange}
              error={emailError}
              required
              helperText=''
              onClear={() => {
                setEmail('');
                setEmailError('');
              }}
              className='w-full'
            />

            {/* Submit Button */}
            <div className='pt-2 sm:pt-3'>
              <Button
                label={isLoading ? 'Sending...' : 'Send Verification Code'}
                variant='primary'
                className='w-full h-11 sm:h-12 text-base sm:text-lg font-semibold bg-g_gradient hover:opacity-90 transition-opacity'
                onClick={handleSubmit}
                disabled={isLoading}
                loading={isLoading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className='mt-5 sm:mt-6 text-center relative z-10'>
            <Typography variant='body2' color='grey' className='text-black text-[14px]'>
              Remember your password?{' '}
              <span 
                className='text-[#1621C9] cursor-pointer underline font-medium'
                onClick={handleBackToLogin}
              >
                Back to Login
              </span>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
